import type { AccessibleWorkspace, WorkspaceLanding } from '@/lib/auth/accessibleWorkspaces';
import { isValidWorkspaceIdFormat } from '@/lib/auth/workspaceAuthority';
import { clearAllSessions } from '@/lib/navigation/navigationMemoryStore';

const CONTEXT_HINT_KEY = 'ax1:context:workspace_hint:v1';

type RequestedContextSource = 'route' | 'query' | 'selector' | 'invitation' | 'onboarding' | 'saved';

export interface WorkspaceContextHint {
  userId: string;
  workspaceId: string;
  organizationId: string;
  updatedAt: string;
  source: RequestedContextSource;
}

type ContextDestination =
  | { kind: 'none' }
  | { kind: 'selector'; requestedWorkspaceId?: string }
  | { kind: 'scoped'; workspaceId: string }
  | { kind: 'denied'; reason: 'requested_workspace_inaccessible' };

interface ResolveDestinationInput {
  landing: WorkspaceLanding;
  requestedWorkspaceId?: string | null;
  source?: RequestedContextSource;
}

const resolutionEpoch = new Map<string, number>();

function parseHint(raw: string | null): WorkspaceContextHint | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as WorkspaceContextHint;
    if (!parsed?.userId || !parsed?.workspaceId || !parsed?.organizationId) return null;
    if (!isValidWorkspaceIdFormat(parsed.workspaceId)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function allAccessible(landing: WorkspaceLanding): AccessibleWorkspace[] {
  if (landing.kind === 'none') return [];
  if (landing.kind === 'one') return [landing.workspace];
  return [...landing.workspaces];
}

export function rememberWorkspaceContextHint(
  userId: string,
  workspace: Pick<AccessibleWorkspace, 'id' | 'organizationId'>,
  source: RequestedContextSource,
): void {
  const next: WorkspaceContextHint = {
    userId,
    workspaceId: workspace.id,
    organizationId: workspace.organizationId,
    source,
    updatedAt: new Date().toISOString(),
  };

  const previous = readWorkspaceContextHint(userId);
  try {
    localStorage.setItem(CONTEXT_HINT_KEY, JSON.stringify(next));
  } catch {
    return;
  }

  if (!previous || previous.workspaceId !== next.workspaceId) {
    clearAllSessions();
  }
}

export function readWorkspaceContextHint(userId: string): WorkspaceContextHint | null {
  try {
    const hint = parseHint(localStorage.getItem(CONTEXT_HINT_KEY));
    if (!hint || hint.userId !== userId) return null;
    return hint;
  } catch {
    return null;
  }
}

export function clearWorkspaceContextHint(userId?: string): void {
  try {
    if (!userId) {
      localStorage.removeItem(CONTEXT_HINT_KEY);
      return;
    }
    const hint = parseHint(localStorage.getItem(CONTEXT_HINT_KEY));
    if (hint && hint.userId === userId) {
      localStorage.removeItem(CONTEXT_HINT_KEY);
    }
  } catch {
    return;
  }
}

export function resolveContextDestination(input: ResolveDestinationInput): ContextDestination {
  const requestedId = input.requestedWorkspaceId && isValidWorkspaceIdFormat(input.requestedWorkspaceId)
    ? input.requestedWorkspaceId
    : null;

  const accessible = allAccessible(input.landing);
  if (requestedId) {
    const matched = accessible.find((workspace) => workspace.id === requestedId);
    if (!matched) {
      return { kind: 'denied', reason: 'requested_workspace_inaccessible' };
    }
    if ((input.source ?? 'saved') === 'saved' && accessible.length > 1) {
      return { kind: 'selector', requestedWorkspaceId: matched.id };
    }
    return { kind: 'scoped', workspaceId: matched.id };
  }

  if (input.landing.kind === 'none') {
    return { kind: 'none' };
  }
  if (input.landing.kind === 'one') {
    return { kind: 'scoped', workspaceId: input.landing.workspace.id };
  }
  return { kind: 'selector' };
}

export function beginContextResolution(userId: string): number {
  const nextEpoch = (resolutionEpoch.get(userId) ?? 0) + 1;
  resolutionEpoch.set(userId, nextEpoch);
  return nextEpoch;
}

export function isCurrentContextResolution(userId: string, token: number): boolean {
  return resolutionEpoch.get(userId) === token;
}
